import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Bell,
  Building2,
  Wrench,
  ArrowLeftRight,
  LogOut,
  Check,
  Wifi,
  WifiOff,
  Menu,
  Repeat,
  Info,
  User,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { formatNotificationTime } from "../notifications/formatTime";
// The reader's language, mapped the way every other date on the product
// surfaces is (BUG-40): "en" here would print 9/18/2026 beside 18.09.2026.
import { uiLocale } from "../../lib/formatDate";
import { useNotifications } from "../../context/NotificationsContext";
import { useSocket } from "../../context/SocketContext";
import { useMobileMenu } from "../../context/MobileMenuContext";
import { useUnsavedChanges } from "../../context/UnsavedChangesContext";
import { toast } from "sonner";
import { dropPushSubscription } from "../../lib/pushLifecycle";
import { Button } from "../ui/button";
import { LanguageSwitcher } from "../ui/language-switcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { getServiceIcon } from "../services/ui";
import { OrgSwitcher } from "./OrgSwitcher";
import { usesDialog, type CurrentEntity, type Membership } from "./switcherLogic";

const API_URL = process.env.REACT_APP_API_URL || "";

export function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { entityType, entityName, isAdmin, logout } = useAuth();
  const { notifications, unreadCount, markRead, markAllRead } =
    useNotifications();
  const { isConnected } = useSocket();
  const { toggle: toggleMobileMenu } = useMobileMenu();
  const { interceptNavigation, guardedNavigate, confirmDiscard } =
    useUnsavedChanges();
  const [backendStatus, setBackendStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const [hasSmartIdSession, setHasSmartIdSession] = useState(false);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [membershipsTruncated, setMembershipsTruncated] = useState(false);
  const [currentEntity, setCurrentEntity] = useState<CurrentEntity>(null);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  /**
   * One lock for every session transition — organisation switch, «Сменить
   * роль», logout (code review r2, finding 2). Each of them rotates or
   * destroys the session and ends in a full navigation, so two in flight race
   * over the cookie and over which navigation wins.
   */
  const [switching, setSwitching] = useState(false);

  // Check backend health periodically
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const response = await fetch('/api/health', {
          method: 'GET',
          cache: 'no-store'
        });
        setBackendStatus(response.ok ? 'online' : 'offline');
      } catch {
        setBackendStatus('offline');
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // The organisations this session may act as (ticket 19). It replaces the old
  // /entities probe: that endpoint answers 401 for admin impersonation, which
  // the global interceptor had to special-case, and it costs a registry-shaped
  // payload the header never used. This one is DB memberships only and answers
  // `principal` for every session kind.
  //
  // On failure: the static pill, no switcher and no «Сменить роль» — the same
  // silence as the probe it replaces. A toast on every cabinet load would be
  // worse than the missing menu item.
  const applyMemberships = useCallback((data: any) => {
    setHasSmartIdSession(data?.principal === "smart_id");
    setMemberships(Array.isArray(data?.memberships) ? data.memberships : []);
    setMembershipsTruncated(data?.truncated === true);
    setCurrentEntity(data?.current ?? null);
  }, []);

  const loadMemberships = useCallback(() => {
    axios
      .get(`${API_URL}/auth/smartid/memberships`, { withCredentials: true })
      .then((res) => applyMemberships(res.data?.data))
      .catch(() => {
        setHasSmartIdSession(false);
        setMemberships([]);
        setMembershipsTruncated(false);
        setCurrentEntity(null);
      });
  }, [applyMemberships]);

  useEffect(() => {
    loadMemberships();
  }, [loadMemberships]);

  const displayName = entityName || "User";
  /**
   * The entity's kind as **three** cases, not two (BUG-M1).
   *
   * It used to be `const isBuilding = entityType === "building"`, feeding both
   * the icon and the role caption through a binary ternary — so a session with
   * no entity at all was *asserted* to be a service provider, wrench icon and
   * all. A role must never be synthesised from its own absence: when there is
   * no entity the honest answer is to say nothing and show a neutral icon.
   *
   * Reachable for a signed-in non-admin principal carrying no entity;
   * `ProtectedRoute` already sends an *admin* without one to /admin/dashboard,
   * so that path never reaches this header.
   */
  const entityKind: "building" | "provider" | null =
    entityType === "building"
      ? "building"
      : entityType === "service_provider"
        ? "provider"
        : null;
  const EntityIcon =
    entityKind === "building" ? Building2 : entityKind === "provider" ? Wrench : User;

  // One organisation is nothing to switch between, and a passport session gets
  // an empty list by design — both keep today's static pill.
  const canSwitchOrg = memberships.length >= 2;
  
  // System status: online if both socket and backend are working
  const systemStatus = backendStatus === 'online' && isConnected ? 'online' 
    : backendStatus === 'checking' ? 'checking'
    : 'offline';

  const recentNotifications = notifications.slice(0, 5);

  const formatTime = (iso: string) =>
    formatNotificationTime(iso, uiLocale(i18n.language), t("notifications.time.justNow"), {
      compact: true,
    });

  // Ask BEFORE the session is destroyed. Logout ends in a full-page navigation,
  // which the guards only meet as the browser's own "Leave site?" prompt — by
  // then the logout POST has already gone out, and "Stay" would leave a signed-in
  // cabinet over a dead session. Cancelling here costs nothing: no request was
  // sent. Confirming discards the drafts first, so that prompt never appears.
  const handleLogout = () => {
    confirmDiscard(() => {
      setSwitching(true);
      // `logout` ends in a full navigation; the reset only matters if it fails
      // and the user stays in a live cabinet.
      void logout().finally(() => setSwitching(false));
    });
  };

  const handleSwitch = () => {
    navigate("/admin/impersonate");
  };

  /**
   * «Сменить роль» / «Новая организация…» → /select-role. It clears the
   * session's entity, so like logout and like a switch it must ask about
   * unsaved work *first*: the browser's own "Leave site?" prompt comes after
   * the POST, and "Stay" would leave the cabinet on screen over a session that
   * no longer has an organisation (code review finding 3).
   */
  const handleSwitchRole = () =>
    confirmDiscard(() => {
      setSwitching(true);
      void runSwitchRole().finally(() => setSwitching(false));
    });

  const runSwitchRole = async () => {
    try {
      // Release the server-side push binding first, while this session still
      // owns it: the row names the entity being left, and after the switch the
      // role picker has no principal to authorize an unsubscribe with (ticket
      // 12, code review r1). The browser subscription is kept, so the sync
      // effect re-binds it as soon as a new role is chosen.
      const released = await dropPushSubscription({
        notifyServer: true,
        keepBrowserSubscription: true,
      });

      // If it did not finish — a slow service worker, a failed request — the
      // row still names the entity being left, and keeping the browser
      // subscription would leave this device receiving that entity's pushes
      // (code review r2, finding 1). Drop the browser subscription instead:
      // the device stops receiving immediately, and the orphaned row reaps
      // itself on the next 410. The user re-enables push from Settings, which
      // is a smaller cost than a stale binding.
      if (!released.done) {
        await dropPushSubscription({ notifyServer: false });
      }

      await axios.post(
        `${API_URL}/auth/smartid/switch-role`,
        {},
        { withCredentials: true }
      );
      window.location.href = "/select-role";
    } catch {
      // Show toast or silent fall-back
    }
  };

  /**
   * The switch. It lives here, not in `OrgSwitcher`, because two instances of
   * that component are mounted (the pill and the mobile picker) and only the
   * header can close both surfaces and disable every competing session
   * transition while one runs (code review finding 2).
   *
   * `POST /auth/smartid/select` *is* the privilege transition — DB membership
   * re-check, sid rotation, socket eviction — and success is a full navigation:
   * route params, drafts, counters, favourites, the socket and the push binding
   * all belong to the organisation being left, and proving in place that every
   * one of them resets is a worse bet than one reload (D3).
   */
  const switchOrganisation = (row: Membership) => {
    // Ask before anything is sent — the switch unmounts the whole cabinet, so
    // it behaves like logout does.
    confirmDiscard(() => {
      setOrgMenuOpen(false);
      setOrgDialogOpen(false);
      setSwitching(true);
      const leaving = currentEntity;
      axios
        .post(
          `${API_URL}/auth/smartid/select`,
          { entityType: row.entityType, entityId: row.entityId },
          { withCredentials: true }
        )
        .then(() => {
          // Synchronously, with nothing in between: no health poll or
          // notifications refresh may paint the new entity's data in the old
          // frame. `replace`, so Back cannot land on a deep link of the
          // organisation that was left.
          window.location.replace("/dashboard");
        })
        .catch(async (err) => {
          const status = err?.response?.status;
          if (status === 401) return; // the global interceptor's
          if (status === 403) {
            // The membership went away between the listing and the click.
            // Nothing rotated: the route refuses before `resetSession`.
            toast.error(t("userMenu.switcher.denied"));
            setSwitching(false);
            loadMemberships();
            return;
          }
          // Anything else is **ambiguous**, not "nothing happened" (code review
          // finding 1): `/select` sends its new cookie with the response
          // headers, so a connection lost after them — or a failure inside
          // `resetSession` itself — can leave the browser authenticated as the
          // new organisation while this frame still shows the old one. Acting
          // in that frame would write to the wrong building, which is the one
          // outcome D3 exists to prevent. So: ask the server who we are, and
          // reload if the answer is not the organisation we are painting.
          try {
            const res = await axios.get(`${API_URL}/auth/smartid/memberships`, {
              withCredentials: true,
            });
            const now = res.data?.data?.current ?? null;
            const moved =
              now?.entityType !== leaving?.entityType || now?.entityId !== leaving?.entityId;
            if (moved) {
              window.location.replace("/dashboard");
              return;
            }
            applyMemberships(res.data?.data);
          } catch {
            // Cannot verify. A reload is noisy; a cabinet that may be acting as
            // another organisation is worse.
            window.location.replace("/dashboard");
            return;
          }
          toast.error(t("userMenu.switcher.failed"));
          setSwitching(false);
        });
    });
  };

  // «Новая организация…» is the existing «Сменить роль» flow — registering,
  // joining and access requests need registry hints and the fresh-login gates
  // the header must not carry (D5).
  const switcherProps = {
    memberships,
    truncated: membershipsTruncated,
    current: currentEntity,
    onSelect: switchOrganisation,
    switching,
    onNewOrganisation: handleSwitchRole,
  };

  return (
    <header className="h-16 bg-sidebar backdrop-blur-xl px-4 lg:px-6 flex items-center justify-between sticky top-0 z-20 border-b border-sidebar-border">
      <div className="flex items-center gap-2 lg:gap-4">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden tap-target"
          onClick={toggleMobileMenu}
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>

        <Link
          to="/dashboard"
          onClick={(e) => {
            if (!interceptNavigation("/dashboard")) e.preventDefault();
          }}
          className="group flex items-center gap-2 shrink-0">
          <img
            src="/logo.png"
            alt="Unikort"
            className="h-9 w-auto"
          />
          <span className="hidden sm:inline text-xl font-semibold text-[#5756d6] dark:text-[#777aff]">
            Unikort
          </span>
        </Link>

        {/* System status indicator */}
        <div
          className="flex items-center gap-1.5 px-2 py-1 rounded-md cursor-default shrink-0"
          title={systemStatus === 'online' ? t('system.online') : systemStatus === 'checking' ? t('system.checking') : t('system.offline')}
        >
          {systemStatus === 'online' ? (
            <Wifi className="h-3.5 w-3.5 text-emerald-500" />
          ) : systemStatus === 'checking' ? (
            <Wifi className="h-3.5 w-3.5 text-amber-500 animate-pulse" />
          ) : (
            <WifiOff className="h-3.5 w-3.5 text-red-500" />
          )}
          <span className={`hidden sm:inline text-xs font-medium ${
            systemStatus === 'online' ? 'text-emerald-600 dark:text-emerald-400' 
            : systemStatus === 'checking' ? 'text-amber-600 dark:text-amber-400'
            : 'text-red-600 dark:text-red-400'
          }`}>
            {systemStatus === 'online' ? 'Online' : systemStatus === 'checking' ? '...' : 'Offline'}
          </span>
        </div>

        {/* The current organisation — static text for a single membership and
            for every passport session, the switcher's trigger otherwise
            (ticket 19). The colon belongs to the sentence, not to a button
            label, so the button uses its own key. */}
        {entityType && !canSwitchOrg && (
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-lg border border-border">
            <EntityIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{t("userMenu.viewingAs")}</span>
            <span className="text-sm font-medium text-foreground">
              {displayName}
            </span>
          </div>
        )}
        {entityType && canSwitchOrg && (
          <OrgSwitcher
            {...switcherProps}
            variant={usesDialog(memberships, membershipsTruncated) ? "dialog" : "menu"}
            open={orgMenuOpen}
            onOpenChange={setOrgMenuOpen}
            trigger={
              <>
                <EntityIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t("userMenu.switcher.label")}
                </span>
                <span className="text-sm font-medium text-foreground truncate max-w-[180px]">
                  {displayName}
                </span>
              </>
            }
          />
        )}
      </div>

      <div className="flex items-center gap-1">
        {/* Language Switcher */}
        <LanguageSwitcher />

        <div className="flex items-center gap-1">
          {/* Mobile: show entity name */}
          <div className="hidden sm:flex md:hidden items-center gap-2 pr-2">
            <EntityIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground truncate max-w-[120px]">
              {displayName}
            </span>
          </div>

          {/* Switch entity — admin impersonation only. Smart-ID users change role
              through "Switch role" in the account menu, which is scoped to their
              own organizations. */}
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSwitch}
              disabled={switching}
              className="gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeftRight className="h-4 w-4" />
              <span className="hidden sm:inline">Switch</span>
            </Button>
          )}

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative rounded-xl">
                <Bell className="w-5 h-5 text-muted-foreground" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              {unreadCount > 0 && (
                <>
                  <DropdownMenuItem
                    onClick={() => markAllRead()}
                    className="gap-2">
                    <Check className="w-4 h-4" />
                    {t("notifications.markAllRead")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}

              {recentNotifications.length === 0 ? (
                <DropdownMenuItem disabled>{t("common.none")}</DropdownMenuItem>
              ) : (
                recentNotifications.map((n) => {
                  const Icon = n.serviceIcon
                    ? getServiceIcon(n.serviceIcon)
                    : Bell;
                  const isUnread = !n.readAt;
                  return (
                    <DropdownMenuItem
                      key={n.id}
                      className="gap-3 items-start py-3"
                      onClick={() => {
                        markRead(n.id);
                        if (n.url) guardedNavigate(n.url);
                      }}>
                      <div>
                        <div className="w-9 h-9 rounded-lg bg-muted/60 border border-border flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-foreground" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium truncate">
                            {n.title}
                          </span>
                          <span className="text-[11px] text-muted-foreground shrink-0">
                            {formatTime(n.createdAt)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {isUnread && (
                            <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                          )}
                          <span className="text-sm text-foreground line-clamp-2">
                            {n.description}
                          </span>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  );
                })
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => guardedNavigate("/notifications")}>
                {t("notifications.viewAll")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => guardedNavigate("/")} className="gap-2">
                <Info className="h-4 w-4" />
                {t("userMenu.aboutPlatform")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full p-0">
                <div className="w-9 h-9 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full flex items-center justify-center ring-2 ring-white/50 dark:ring-white/10">
                  <EntityIcon className="h-4 w-4 text-foreground" />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-2">
                <div className="flex items-center gap-2">
                  <EntityIcon className="h-4 w-4 text-muted-foreground" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{displayName}</span>
                    {entityKind && (
                      <span className="text-xs text-muted-foreground">
                        {entityKind === "building"
                          ? t("userMenu.roleBuilding")
                          : t("userMenu.roleProvider")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <DropdownMenuSeparator />
              {/* «Сменить объект» was removed outright (BUG-M1, customer's
                  decision 2026-10-09): it called `handleSwitch`, which is the
                  admin impersonation the «Switch» button in the bar already
                  does, so the menu item was a second door to one room. Its
                  `switchEntity` string went with it in all three locales — a
                  key nothing asks for is not a translation, it is litter. A Smart-ID principal changes organisation through
                  «Новая организация…» and «Сменить роль» just below. */}
              {/* The pill is hidden below md, so the menu is where a phone
                  switches organisation — one item that opens the same picker,
                  never a list nested inside a 224 px menu. */}
              {canSwitchOrg && (
                <DropdownMenuItem
                  onClick={() => setOrgDialogOpen(true)}
                  disabled={switching}
                  className="gap-2">
                  <ArrowLeftRight className="w-4 h-4" />
                  {t("userMenu.switcher.changeOrg")}
                </DropdownMenuItem>
              )}
              {hasSmartIdSession && (
                <DropdownMenuItem
                  onClick={handleSwitchRole}
                  disabled={switching}
                  className="gap-2">
                  <Repeat className="w-4 h-4" />
                  {t("userMenu.switchRole")}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                disabled={switching}
                className="gap-2 text-destructive focus:text-destructive">
                <LogOut className="w-4 h-4" />
                {t("auth.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/*
        A session transition is in flight. The header's own controls are
        disabled above, but the cabinet behind it stayed editable: a draft
        typed while `/select` is pending arms `beforeunload`, and cancelling
        that prompt leaves organisation A on screen over B's session — the
        cross-organisation write D3 exists to prevent (code review r2,
        finding 1). Blocking the frame for the sub-second the request takes is
        far cheaper than teaching every form about the transition, and it
        doubles as the feedback that something is happening.
      */}
      {switching && (
        <div
          className="fixed inset-0 z-[60] cursor-wait bg-background/40"
          aria-busy="true"
          aria-label={t("userMenu.switcher.aria")}
          data-testid="session-transition-block"
        />
      )}

      {/* The mobile picker. Trigger-less and controlled by the menu item above;
          the pill renders its own instance. */}
      {canSwitchOrg && (
        <OrgSwitcher
          {...switcherProps}
          variant="dialog"
          open={orgDialogOpen}
          onOpenChange={setOrgDialogOpen}
        />
      )}
    </header>
  );
}
